import React, { useEffect, useState } from "react";
import { Banknote, Check, CheckCircle2, History, Loader2, Pencil, Split, AlertTriangle, Mail, Eye } from "lucide-react";
import { api, argent } from "./api";
import { useConfirmation } from "./Confirmation";
import { Badge, BarreSelection, BoutonSombre, CarteIcone, Case, ChampTexte, Modale, Puces, Recherche, Section, Tableau, Cellule } from "./ui";

/* Facturation › Cash / espèces (20.10.2026). L'école : « les seules actions c'est confirmer
   l'encaissement » pour les cartes 10 cours et les workshops, « le bouton payé en espèces
   n'est pas aligné avec confirmer l'encaissement comme les autres », « ce devrait être les
   mêmes boutons pour toutes les sections ». Désormais UNE liste pour tout ce qui se paie au
   comptoir — factures de cours, stages, cartes 10 cours, workshops heels, dettes d'un autre
   semestre —, les mêmes colonnes et les quatre mêmes boutons, toujours à la même place :
   Encaisser · Modifier · Échelonner · Historique. La liste vient du serveur
   (/facturation/especes?tout=true), la même qu'À traiter. */

export const GENRES_CASH = [
  { valeur: "tous", label: "Tout" },
  { valeur: "facture", label: "Cours semestriels" },
  { valeur: "stage", label: "Stages" },
  { valeur: "carte_10", label: "Cartes 10 cours" },
  { valeur: "workshop", label: "Workshops heels" },
];
const BADGES = { facture: ["Cours", "bleu"], stage: ["Stage", "vert"], carte_10: ["Carte 10 cours", "ambre"], workshop: ["Workshop heels", "rouge"] };
const HORS_FACTURE = ["carte_10", "workshop"];

const jourFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const nombre = (v) => (v === "" || v === null || v === undefined ? NaN : Number(String(v).replace(",", ".")));

/* Les quatre boutons, dans une grille 2 × 2 : chacun a sa place fixe, d'une ligne à
   l'autre — Encaisser toujours en haut à gauche. */
const ActionsCash = ({ it, onEncaisser, onModifier, onEchelonner, onHistorique }) => {
  const solde = it.reste <= 0.02;
  const bouton = { padding: "6px 8px", fontSize: 12, gap: 5, justifyContent: "center", width: "100%", boxShadow: "none" };
  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(2, 112px)" }} data-testid="cash-actions">
      {solde ? (
        <span className="inline-flex items-center justify-center gap-1 rounded-full text-[12px] font-semibold"
          style={{ background: "#F0FDF4", color: "#15803D", border: "1px solid #BBF7D0" }} data-testid="cash-encaisse">
          <Check size={12} /> Encaissé
        </span>
      ) : (
        <button className="btn-primary" style={bouton} data-testid="cash-encaisser" onClick={() => onEncaisser(it)}>
          <Banknote size={12} /> Encaisser
        </button>
      )}
      <button className="btn-ghost" style={bouton} data-testid="cash-modifier" onClick={() => onModifier(it)}
        title={it.genre === "facture" ? "Montants, rabais, mode de paiement" : it.genre === "stage"
          ? "Prorata, rabais, jours suivis, mode de paiement" : it.genre === "carte_10"
            ? "Rabais, mode de paiement (cash ou facture)" : "Rabais sur le prix"}>
        <Pencil size={12} /> Modifier
      </button>
      <button className="btn-ghost" style={bouton} data-testid="cash-echelonner" onClick={() => onEchelonner(it)}
        disabled={solde && !it.echelonnement}>
        <Split size={12} /> {it.echelonnement ? `Échelonné ${it.echelonnement.nb}×` : "Échelonner"}
      </button>
      <button className="btn-ghost" style={bouton} data-testid="cash-historique" onClick={() => onHistorique(it)}>
        <History size={12} /> Historique
      </button>
    </div>
  );
};

/* Les états d'une ligne au comptoir : les filtres de l'écran, comme les vues de Facturation.
   « À encaisser » = ce qui attend encore de l'argent, le même nombre qu'À traiter. */
export const ETATS_CASH = [
  { valeur: "a_encaisser", label: "À encaisser", garde: (it) => it.a_confirmer !== false },
  { valeur: "partiel", label: "Payé en partie", garde: (it) => it.a_confirmer !== false && it.recu > 0.004 },
  { valeur: "echelonne", label: "Échelonnés", garde: (it) => it.a_confirmer !== false && !!it.echelonnement },
  { valeur: "encaisse", label: "Encaissé", garde: (it) => it.a_confirmer === false },
  { valeur: "tout", label: "Tout", garde: () => true },
];
const TRIS = {
  nom: { label: "Nom (A → Z)", cle: (a, b) => String(a.titre).localeCompare(String(b.titre), "fr") },
  reste: { label: "Reste dû (le plus élevé)", cle: (a, b) => (b.reste || 0) - (a.reste || 0) },
  sorte: { label: "Sorte (cours, stage, carte, workshop)",
    cle: (a, b) => GENRES_CASH.findIndex((g) => g.valeur === a.genre) - GENRES_CASH.findIndex((g) => g.valeur === b.genre)
      || String(a.titre).localeCompare(String(b.titre), "fr") },
};

/* Facturation › Cash / espèces : un écran à part, à côté des parrainages (20.10.2026,
   « un menu à part pour le cash, pour trier ce qui est payé et ce qui ne l'est pas ») — ce
   n'est pas un état de facture mais un autre circuit. En tête, trois chiffres cliquables ;
   puis l'état (à encaisser, payé en partie, échelonnés, encaissé, tout), la sorte (cours,
   stages, cartes 10 cours, workshops), une recherche et un tri. */
export default function VueCash({ liste, genreDepart = "tous", occupe,
  onEncaisserLot, onOuvrir, onChange, onVoirFacture, onVoirCartes }) {
  const [genre, setGenre] = useState(genreDepart);
  const [etat, setEtat] = useState("a_encaisser");
  const [cherche, setCherche] = useState("");
  const [tri, setTri] = useState("nom");
  const [choisis, setChoisis] = useState([]);
  const [encaisser, setEncaisser] = useState(null);
  const [rabais, setRabais] = useState(null);
  const [echelon, setEchelon] = useState(null);
  const [histo, setHisto] = useState(null);
  useEffect(() => { setGenre(genreDepart); }, [genreDepart]);

  const items = liste?.items || [];
  const mot = cherche.trim().toLowerCase();
  const garde = (ETATS_CASH.find((e) => e.valeur === etat) || ETATS_CASH[0]).garde;
  const dansGenre = (it, g = genre) => g === "tous" || it.genre === g;
  const trouve = (it) => !mot || `${it.titre} ${it.sous || ""} ${it.quoi || ""}`.toLowerCase().includes(mot);
  const lignes = items.filter((it) => garde(it) && dansGenre(it) && trouve(it)).sort(TRIS[tri].cle);
  // Chaque filtre compte ce que l'autre laisse passer : l'état dans la sorte choisie, la
  // sorte dans l'état choisi.
  const nbEtat = (e) => items.filter((it) => e.garde(it) && dansGenre(it) && trouve(it)).length;
  const nbGenre = (g) => items.filter((it) => garde(it) && dansGenre(it, g) && trouve(it)).length;
  const attendus = items.filter((it) => it.a_confirmer !== false);
  const partiels = attendus.filter((it) => it.recu > 0.004);
  const somme = (l, k) => l.reduce((t, it) => t + (it[k] || 0), 0);
  const aEncaisser = lignes.filter((it) => it.a_confirmer !== false);
  const tousCoches = aEncaisser.length > 0 && aEncaisser.every((it) => choisis.includes(it.ref));
  const choisir = (e) => { setEtat(e); setChoisis([]); };

  const modifier = (it) => (HORS_FACTURE.includes(it.genre) ? setRabais(it) : onOuvrir(it, "modifier"));
  const echelonner = (it) => (it.genre === "facture" ? onOuvrir(it, "echelonner") : setEchelon(it));
  const apres = async () => { setChoisis([]); await onChange(); };

  return (
    <Section titre="Cash / espèces — paiements au comptoir" testid="vue-cash"
      aide="Tout ce qui se paie au comptoir, au même endroit : factures de cours et de stages réglées en espèces, cartes 10 cours et workshops heels (espèces ou TWINT, jamais facturés), dettes d'un autre semestre. « Encaisser » prend tout le reste ou une partie seulement : la ligne reste « à encaisser » tant que tout n'est pas reçu. Plus au comptoir ? « Modifier » → mode de paiement « Virement » : la facture repart dans « À générer ».">
      <div className="grid sm:grid-cols-3 gap-4" data-testid="cash-resume">
        <CarteIcone titre="À encaisser" valeur={argent(somme(attendus, "reste"))} Icon={Banknote} teinte="ambre"
          detail={`${attendus.length} ligne(s) attendent de l'argent`} onClick={() => choisir("a_encaisser")}
          testid="cash-carte-a-encaisser" />
        <CarteIcone titre="Payé en partie" valeur={partiels.length} Icon={Split} teinte="bleu"
          detail={`${argent(somme(partiels, "recu"))} reçus · ${argent(somme(partiels, "reste"))} restent`}
          onClick={() => choisir("partiel")} testid="cash-carte-partiel" />
        <CarteIcone titre="Déjà reçu au comptoir" valeur={argent(somme(items, "recu"))} Icon={CheckCircle2} teinte="vert"
          detail={`${items.filter((it) => it.a_confirmer === false).length} ligne(s) encaissée(s) en entier`}
          onClick={() => choisir("encaisse")} testid="cash-carte-encaisse" />
      </div>

      <div className="space-y-2">
        <Puces testid="cash-etats" valeur={etat} onChange={choisir}
          options={ETATS_CASH.map((e) => ({ valeur: e.valeur, label: e.label, nb: nbEtat(e), testid: `cash-etat-${e.valeur}` }))} />
        <div className="flex flex-wrap items-center gap-3">
          <Puces testid="cash-genres" valeur={genre} onChange={(v) => { setGenre(v); setChoisis([]); }}
            options={GENRES_CASH.map((g) => ({ ...g, nb: nbGenre(g.valeur), testid: `cash-genre-${g.valeur}` }))} />
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <select className="input-field text-xs" style={{ width: 230, padding: "8px 12px" }} value={tri}
              onChange={(e) => setTri(e.target.value)} data-testid="cash-tri" aria-label="Trier">
              {Object.entries(TRIS).map(([k, v]) => <option key={k} value={k}>Trier : {v.label}</option>)}
            </select>
            <Recherche valeur={cherche} onChange={setCherche} largeur={260} testid="cash-recherche"
              placeholder="Nom, élève, cours…" />
          </div>
        </div>
        {/* Les cartes payées avec la facture du semestre ne passent pas par le comptoir : on
            le dit, et on renvoie vers la liste de toutes les cartes (Jen, 30.10.2026). */}
        {genre === "carte_10" && liste?.cartes_10_sur_facture > 0 && (
          <p className="text-xs text-[var(--muted)]" data-testid="cash-cartes-sur-facture">
            + {liste.cartes_10_sur_facture} carte{liste.cartes_10_sur_facture > 1 ? "s" : ""} 10 cours payée{liste.cartes_10_sur_facture > 1 ? "s" : ""} avec la facture du semestre (pas au comptoir, donc pas ici).
            {onVoirCartes && <> <button type="button" className="underline" onClick={onVoirCartes} data-testid="cash-voir-cartes">Voir toutes les cartes 10 cours →</button></>}
          </p>
        )}
      </div>

      {aEncaisser.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <Case cochee={tousCoches} testid="cash-tout-cocher" titre="Tout sélectionner"
            onChange={(v) => setChoisis(v ? aEncaisser.map((it) => it.ref) : [])} />
          <span className="text-xs text-[var(--muted)]">Tout sélectionner ({aEncaisser.length})</span>
        </div>
      )}
      {choisis.length > 0 && (
        <BarreSelection nb={choisis.length} testid="cash-barre">
          <span className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
            {argent(items.filter((it) => choisis.includes(it.ref)).reduce((t, it) => t + it.reste, 0))}
          </span>
          <BoutonSombre disabled={occupe === "especes"} testid="fact-confirmer-especes"
            onClick={async () => { await onEncaisserLot(items.filter((it) => choisis.includes(it.ref))); setChoisis([]); }}>
            {occupe === "especes" ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
            Encaisser en entier
          </BoutonSombre>
        </BarreSelection>
      )}

      <Tableau
        testid="tableau-cash"
        entetes={["", { label: "Qui", largeur: "24%" }, { label: "Quoi", large: true },
          { label: "Total", droite: true }, { label: "Reçu", droite: true }, { label: "Reste", droite: true }, ""]}
        lignes={lignes.map((it) => ({ ...it, id: it.ref }))}
        vide={etat === "a_encaisser" && !mot && genre === "tous" ? "Rien à encaisser : tout est arrivé." : "Aucune ligne pour ces filtres."}
        rendu={(it) => {
          const [badge, ton] = BADGES[it.genre] || ["", "bleu"];
          return (
            <>
              <Cellule>
                {it.a_confirmer !== false && (
                  <Case cochee={choisis.includes(it.ref)} testid="cash-case"
                    onChange={(v) => setChoisis(v ? [...choisis, it.ref] : choisis.filter((x) => x !== it.ref))} />
                )}
              </Cellule>
              <Cellule fort>
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {it.titre || "—"} <Badge ton={ton}>{badge}</Badge>
                </span>
                {it.sous && <div className="text-xs font-normal text-[var(--muted)] mt-0.5">{it.sous}</div>}
              </Cellule>
              <Cellule muet>
                <div>{it.quoi || "—"}</div>
                {it.quand && it.quand !== "stage" && (
                  <div className="text-[11px]" style={it.saison_close ? { color: "#B45309" } : undefined}>{it.quand}</div>
                )}
                {it.rabais > 0.004 && (
                  <div className="text-[11px] mt-0.5" style={{ color: "#047857" }} data-testid="cash-rabais">
                    rabais −{argent(it.rabais)}{it.motif_rabais ? ` · ${it.motif_rabais}` : ""}
                  </div>
                )}
                {it.echelonnement && (
                  <div className="text-[11px] mt-0.5" style={{ color: "#1D4ED8" }} data-testid="cash-echelonnement">
                    Échelonné {it.echelonnement.nb}× · {it.echelonnement.recus}/{it.echelonnement.nb} reçu(s)
                    {it.echelonnement.prochaine && it.reste > 0.02 ? ` · prochain le ${jourFr(it.echelonnement.prochaine)}` : ""}
                  </div>
                )}
                {it.envoyee_le && (
                  <div className="text-[11px] mt-0.5 font-semibold" style={{ color: "#B45309" }} data-testid="cash-facture-partie">
                    ⚠ une facture QR est partie le {jourFr(it.envoyee_le)} — voir l'historique
                  </div>
                )}
              </Cellule>
              <Cellule droite><span className="whitespace-nowrap">{argent(it.total)}</span></Cellule>
              <Cellule droite muet><span className="whitespace-nowrap">{it.recu > 0.004 ? argent(it.recu) : "—"}</span></Cellule>
              <Cellule droite fort>
                <span className="whitespace-nowrap" style={{ color: it.reste > 0.02 ? "#B45309" : "#15803D" }}>
                  {it.reste > 0.02 ? argent(it.reste) : "0"}
                </span>
              </Cellule>
              <Cellule largeur={236}>
                <ActionsCash it={it} onEncaisser={setEncaisser} onModifier={modifier}
                  onEchelonner={echelonner} onHistorique={setHisto} />
              </Cellule>
            </>
          );
        }}
      />

      {encaisser && <ModaleEncaisser it={encaisser} onFermer={() => setEncaisser(null)}
        onOk={async () => { setEncaisser(null); await apres(); }} />}
      {rabais && <ModaleRabais it={rabais} onFermer={() => setRabais(null)}
        onOk={async () => { setRabais(null); await apres(); }} />}
      {echelon && <ModaleEchelonnerCash it={echelon} onFermer={() => setEchelon(null)}
        onOk={async () => { setEchelon(null); await apres(); }} />}
      {histo && <ModaleHistoriqueCash it={histo} onFermer={() => setHisto(null)} onVoirFacture={onVoirFacture}
        onOk={async () => { setHisto(null); await apres(); }} />}
    </Section>
  );
}

/* Trois chiffres en tête des fenêtres : ce que ça coûte, ce qui est arrivé, ce qui manque. */
const TroisChiffres = ({ it }) => (
  <div className="grid grid-cols-3 gap-2 mb-4">
    {[["Total", it.total, "var(--text)"], ["Déjà reçu", it.recu, "#15803D"], ["Reste", it.reste, "#B45309"]].map(([t, v, c]) => (
      <div key={t} className="rounded-xl px-3 py-2" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}>
        <div className="text-[11px] text-[var(--muted)]">{t}</div>
        <div className="font-display text-xl" style={{ color: c }}>{argent(v)}</div>
      </div>
    ))}
  </div>
);

/* « Encaisser » : tout le reste, ou une partie (une enveloppe d'un paiement en plusieurs
   fois). La ligne reste dans Cash / espèces tant que tout n'est pas arrivé. */
export function ModaleEncaisser({ it, onFermer, onOk }) {
  const part = it.echelonnement && it.echelonnement.part ? Math.min(it.echelonnement.part, it.reste) : null;
  const [partiel, setPartiel] = useState(!!part && part < it.reste - 0.02);
  const [montant, setMontant] = useState(part ? String(part) : "");
  const [jour, setJour] = useState(aujourdhui());
  const [mode, setMode] = useState("Espèces");
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const m = partiel ? nombre(montant) : it.reste;
  const valide = Number.isFinite(m) && m > 0 && m <= it.reste + 0.02;

  const valider = async () => {
    if (!valide) { setErr(`Un montant entre 0 et ${argent(it.reste)}.`); return; }
    setEnvoi(true);
    setErr("");
    try {
      await api("/facturation/especes/encaisser", { method: "POST", body: JSON.stringify({
        ref: it.ref, montant: partiel ? m : null, date_paiement: jour,
        mode: HORS_FACTURE.includes(it.genre) ? mode : "" }) });
      await onOk();
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };

  return (
    <Modale titre={`Encaisser — ${it.titre}`} sous_titre={it.quoi} onFermer={onFermer} testid="modale-encaisser">
      <TroisChiffres it={it} />
      <div className="space-y-2 mb-4" role="radiogroup">
        {[[false, `Tout le reste — ${argent(it.reste)}`], [true, "Une partie seulement"]].map(([v, l]) => (
          <label key={l} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" checked={partiel === v} onChange={() => setPartiel(v)}
              data-testid={v ? "encaisser-partie" : "encaisser-tout"} />
            {l}
          </label>
        ))}
      </div>
      {partiel && (
        <ChampTexte label="Montant reçu aujourd'hui (CHF)" valeur={montant} onChange={setMontant} testid="encaisser-montant"
          aide={valide && m < it.reste - 0.02
            ? `Il restera ${argent(it.reste - m)} : la ligne reste dans Cash / espèces.` : "jamais plus que le reste"} />
      )}
      <ChampTexte label="Date" type="date" valeur={jour} onChange={setJour} testid="encaisser-date" />
      {HORS_FACTURE.includes(it.genre) && (
        <div className="mb-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-1">Payé en</div>
          <Puces testid="encaisser-mode" valeur={mode} onChange={setMode}
            options={[{ valeur: "Espèces", label: "Espèces" }, { valeur: "TWINT", label: "TWINT" }]} />
        </div>
      )}
      {err && <p className="text-sm mb-3" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="flex items-center gap-2">
        <BoutonSombre onClick={valider} disabled={envoi || !valide} testid="encaisser-valider">
          {envoi ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
          Encaisser {valide ? argent(m) : ""}
        </BoutonSombre>
        <button className="btn-ghost" onClick={onFermer}>Annuler</button>
      </div>
    </Modale>
  );
}

/* « Modifier » d'une carte 10 cours ou d'un workshop : un rabais sur le prix, avec son
   motif (gardé dans l'historique), et le mode de paiement — une carte peut passer sur la
   facture du semestre de la famille (20.10.2026) ; un workshop reste au comptoir. Les
   factures ont leur propre fenêtre de montants. */
function ModaleRabais({ it, onFermer, onOk }) {
  const prix = Math.round(((it.prix || 0) + (it.taxe || 0)) * 100) / 100;
  const [rabais, setRabais] = useState(it.rabais ? String(it.rabais) : "");
  const [motif, setMotif] = useState(it.motif_rabais || "");
  const [mode, setMode] = useState("cash");
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const r = rabais === "" ? 0 : nombre(rabais);
  const nouveau = Math.round((prix - (Number.isFinite(r) ? r : 0)) * 100) / 100;
  const carte = it.genre === "carte_10";
  const rabaisChange = Math.abs((r || 0) - (it.rabais || 0)) > 0.004 || motif !== (it.motif_rabais || "");

  const valider = async () => {
    setEnvoi(true);
    setErr("");
    try {
      if (rabaisChange) {
        await api("/facturation/especes/rabais", { method: "POST", body: JSON.stringify({ ref: it.ref, rabais: r || 0, motif }) });
      }
      if (mode === "facture") {
        await api("/facturation/especes/mode", { method: "POST", body: JSON.stringify({ ref: it.ref, mode: "facture" }) });
      }
      await onOk();
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };

  return (
    <Modale titre={`Modifier — ${it.titre}`} sous_titre={it.quoi} onFermer={onFermer} testid="modale-rabais">
      <p className="text-sm mb-4">
        Prix : <strong>{argent(prix)}</strong>
        {it.taxe > 0.004 ? ` (dont ${argent(it.taxe)} de taxe d'inscription)` : ""}
        {it.recu > 0.004 ? ` · déjà reçu ${argent(it.recu)}` : ""}
      </p>
      <ChampTexte label="Rabais (CHF)" valeur={rabais} onChange={setRabais} testid="rabais-montant"
        aide={Number.isFinite(r) && r >= 0 ? `nouveau prix : ${argent(nouveau)}` : "un montant en francs"} />
      <ChampTexte label="Motif" valeur={motif} onChange={setMotif} testid="rabais-motif"
        aide="par exemple « fratrie », « geste commercial » — gardé dans l'historique" />
      <div className="mb-4" data-testid="rabais-mode">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-1">Mode de paiement</div>
        {carte && it.recu > 0.004 ? (
          <p className="text-xs text-[var(--muted)]" data-testid="rabais-mode-bloque">
            Cash — au comptoir. Déjà en partie encaissée : pour la passer sur la facture, annule
            d'abord l'encaissement (Historique).
          </p>
        ) : carte ? (
          <>
            <Puces testid="rabais-mode-choix" valeur={mode} onChange={setMode}
              options={[{ valeur: "cash", label: "Cash — au comptoir" }, { valeur: "facture", label: "Facture QR" }]} />
            <p className="text-xs mt-2" style={{ color: mode === "facture" ? "#1D4ED8" : "var(--muted)" }}>
              {mode === "facture"
                ? `La carte passe sur la facture du semestre de la famille, à ${argent(nouveau)} : elle quitte Cash / espèces et la facture repart dans « À générer ».`
                : "Réglée au comptoir (espèces ou TWINT), sans facture."}
            </p>
          </>
        ) : (
          <p className="text-xs text-[var(--muted)]">
            Toujours au comptoir (TWINT ou espèces) : un workshop heels n'a pas de facture.
          </p>
        )}
      </div>
      {err && <p className="text-sm mb-3" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="flex items-center gap-2">
        <BoutonSombre onClick={valider} disabled={envoi || !Number.isFinite(r) || r < 0 || (!rabaisChange && mode === "cash")}
          testid="rabais-valider">
          {envoi ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />} Enregistrer
        </BoutonSombre>
        <button className="btn-ghost" onClick={onFermer}>Annuler</button>
      </div>
    </Modale>
  );
}

/* « Échelonner » au comptoir : l'accord est noté (2 ou 3 enveloppes, la première
   aujourd'hui, puis une par mois) ; chaque enveloppe s'encaisse avec « Encaisser ». */
export function ModaleEchelonnerCash({ it, onFermer, onOk, explication }) {
  const [nb, setNb] = useState(String(it.echelonnement?.nb || 2));
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const enregistrer = async (n) => {
    setEnvoi(true);
    setErr("");
    try {
      await api("/facturation/especes/echelonner", { method: "POST", body: JSON.stringify({ ref: it.ref, nb: n }) });
      await onOk();
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };
  return (
    <Modale titre={`Paiement en plusieurs fois — ${it.titre}`} sous_titre={it.quoi} onFermer={onFermer} testid="modale-echelonner-cash">
      <TroisChiffres it={it} />
      <Puces testid="echelonner-nb" valeur={nb} onChange={setNb}
        options={[{ valeur: "2", label: "2 fois" }, { valeur: "3", label: "3 fois" }]} />
      <p className="text-xs text-[var(--muted)] my-4">
        Environ <strong>{argent(it.total / Number(nb))}</strong> par versement : le premier aujourd'hui, puis un
        par mois. {explication || <>Chaque enveloppe reçue s'encaisse avec « Encaisser » (une partie) ; la ligne dit
        où elle en est (« 1/{nb} reçu »).</>}
      </p>
      {err && <p className="text-sm mb-3" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
      <div className="flex items-center gap-2">
        <BoutonSombre onClick={() => enregistrer(Number(nb))} disabled={envoi} testid="echelonner-enregistrer">
          {envoi ? <Loader2 size={14} className="animate-spin" /> : <Split size={14} />} Enregistrer l'accord
        </BoutonSombre>
        {it.echelonnement && (
          <button className="btn-ghost" onClick={() => enregistrer(0)} disabled={envoi} data-testid="echelonner-annuler">
            Annuler l'accord
          </button>
        )}
      </div>
    </Modale>
  );
}

/* « Historique » : ce qui est arrivé à cette ligne. D'abord les factures et rappels partis
   par e-mail — une famille au comptoir ne devrait en recevoir aucun, certaines en ont reçu
   par mégarde —, puis les encaissements et tout ce qui a changé. */
function ModaleHistoriqueCash({ it, onFermer, onOk, onVoirFacture }) {
  const { demander } = useConfirmation();
  const [h, setH] = useState(null);
  const [err, setErr] = useState("");
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    api(`/facturation/especes/historique?ref=${encodeURIComponent(it.ref)}`).then(setH).catch((e) => setErr(e.message));
  }, [it.ref]);
  const quand = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? jourFr(iso)
      : `${d.toLocaleDateString("fr-CH", { timeZone: "Europe/Zurich" })} ${d.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zurich" })}`;
  };

  const annuler = async () => {
    if (!await demander({
      titre: `Annuler l'encaissement — ${it.titre}`, ton: "danger",
      texte: `${argent(it.recu)} reçus repartent à zéro : la ligne attend de nouveau ${argent(it.total)}.`,
      points: ["À n'utiliser que pour corriger une erreur de saisie.",
        "Un virement déjà pointé depuis le relevé bancaire reste compté."],
      valider: "Annuler l'encaissement", annuler: "Ne rien changer",
    })) return;
    setEnvoi(true);
    try {
      await api("/facturation/especes/annuler", { method: "POST", body: JSON.stringify({ ref: it.ref }) });
      await onOk();
    } catch (e) {
      setErr(e.message);
      setEnvoi(false);
    }
  };

  return (
    <Modale large titre={`Historique — ${it.titre}`} sous_titre={it.quoi} onFermer={onFermer} testid="modale-historique-cash">
      <TroisChiffres it={it} />
      {err && <p className="text-sm mb-3" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
      {!h && !err && <p className="text-sm text-[var(--muted)]">Chargement…</p>}
      {h && (
        <div className="space-y-5">
          {!HORS_FACTURE.includes(it.genre) && (
            <div data-testid="historique-emails">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-2 flex items-center gap-1.5">
                <Mail size={11} /> Factures et rappels envoyés par e-mail
              </div>
              {h.emails.length === 0 ? (
                <p className="text-sm text-[var(--muted)]">Aucun : rien n'est parti par e-mail, comme il se doit au comptoir.</p>
              ) : (
                <>
                  <div className="rounded-xl px-3 py-2 mb-2 text-xs flex items-start gap-2"
                    style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}>
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    Cette personne paie au comptoir, mais {h.emails.length} e-mail(s) de facture ou de rappel sont partis.
                    Si c'était par mégarde, préviens-la que la facture QR ne compte pas : elle règle ici.
                  </div>
                  <ul className="space-y-1">
                    {h.emails.map((m, k) => (
                      <li key={k} className="text-sm flex flex-wrap gap-x-2">
                        <span className="text-[var(--muted)] whitespace-nowrap">{quand(m.le)}</span>
                        <strong>{m.quoi}</strong>
                        <span>n° {m.numero}</span>
                        <span className="text-[var(--muted)]">à {m.a}{m.simule ? " (mode test : pas parti)" : ""}{m.echec ? " — refusé" : ""}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {onVoirFacture && it.genre === "facture" && (
                <button className="btn-ghost mt-2" style={{ padding: "6px 10px", fontSize: 12 }}
                  data-testid="historique-voir-facture" onClick={() => onVoirFacture(it)}>
                  <Eye size={12} /> Voir la facture et ses versions
                </button>
              )}
            </div>
          )}
          {(h.versements || []).length > 0 && (
            <div data-testid="historique-versements">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-2">Versements reçus</div>
              <ul className="space-y-1">
                {h.versements.map((v, k) => (
                  <li key={k} className="text-sm">
                    <span className="text-[var(--muted)]">{jourFr(v.le)}</span> · <strong>{argent(v.montant)}</strong>
                    {v.mode ? ` · ${v.mode}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div data-testid="historique-evenements">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)] mb-2">Encaissements et changements</div>
            {h.evenements.length === 0 ? <p className="text-sm text-[var(--muted)]">Rien de noté pour l'instant.</p> : (
              <ul className="space-y-2">
                {h.evenements.map((ev, k) => (
                  <li key={k} className="text-sm rounded-xl px-3 py-2" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}>
                    <div className="flex flex-wrap gap-x-2">
                      <span className="text-[var(--muted)] whitespace-nowrap">{quand(ev.le)}</span>
                      <strong>{ev.quoi}</strong>
                      {ev.par && <span className="text-[var(--muted)]">· {ev.par}</span>}
                    </div>
                    {(ev.detail || []).length > 0 && (
                      <div className="text-xs text-[var(--muted)] mt-0.5">{ev.detail.join(" · ")}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-5">
        {it.recu > 0.004 && (
          <button className="btn-ghost" style={{ color: "#B91C1C" }} onClick={annuler} disabled={envoi}
            data-testid="historique-annuler-encaissement">
            Annuler l'encaissement
          </button>
        )}
        <button className="btn-ghost ml-auto" onClick={onFermer}>Fermer</button>
      </div>
    </Modale>
  );
}
